import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp, Plus, Search } from "lucide-react"

import { cn } from "../../lib/utils"

// Every dropdown in the app is searchable: SelectContent shows a filter box
// once a list is long enough to need one, and SelectItem hides itself when
// its text does not match. Hidden items stay mounted (display:none + disabled)
// rather than unmounting, because Radix paints the trigger's text from the
// selected item's ItemText — unmounting it would blank the trigger.
const SEARCH_FROM = 6
const SearchContext = React.createContext("")

const rawText = (node) => {
  if (node == null || typeof node === "boolean") return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(rawText).join("")
  return rawText(node.props?.children)
}
const textOf = (node) => rawText(node).replace(/\s+/g, " ").trim()

// "Add new": with `addLabel`, typing something that matches no option offers
// one more row, `+ <addLabel> "typed"`. Choosing it calls the Root's
// onValueChange with ADD_PREFIX + the text; `addedValue` reads it back.
const ADD_PREFIX = "__add__:"
const addedValue = (value) =>
  typeof value === "string" && value.startsWith(ADD_PREFIX) ? value.slice(ADD_PREFIX.length) : null

const itemTexts = (node) => {
  if (node == null || typeof node !== "object") return []
  if (Array.isArray(node)) return node.flatMap(itemTexts)
  if (node.type === SelectItem) return [textOf(node.props?.children)]
  return itemTexts(node.props?.children)
}

const countItems = (node) => {
  if (node == null || typeof node !== "object") return 0
  if (Array.isArray(node)) return node.reduce((n, c) => n + countItems(c), 0)
  if (node.type === SelectItem) return 1
  return countItems(node.props?.children)
}

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-10 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-muted/60 px-3.5 py-2 text-sm text-foreground ring-offset-background transition-all duration-200 data-[placeholder]:text-muted-foreground/70 hover:border-slate-300 hover:bg-muted focus:outline-none focus:ring-4 focus:ring-primary/15 focus:border-primary disabled:cursor-not-allowed disabled:opacity-60 [&>span]:line-clamp-1",
      className
    )}
    {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectSearch = ({ value, onChange }) => {
  const inputRef = React.useRef(null)
  React.useEffect(() => {
    // Radix focuses the selected item on open; take it after that tick.
    const t = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [])
  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-popover px-2 py-1.5">
      <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search…"
        aria-label="Search options"
        className="h-7 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
        onKeyDown={(e) => {
          // Arrows, Enter, Escape and Tab belong to the list; everything else is typing.
          if (!["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab", "Home", "End"].includes(e.key)) e.stopPropagation()
        }}
        onPointerDown={(e) => e.stopPropagation()}
      />
    </div>
  )
}

const SelectContent = React.forwardRef(({ className, children, position = "popper", searchable, addLabel, ...props }, ref) => {
  const [query, setQuery] = React.useState("")
  const showSearch = Boolean(addLabel) || (searchable ?? countItems(children) >= SEARCH_FROM)
  const typed = query.trim()
  const canAdd = Boolean(addLabel) && typed !== "" &&
    !itemTexts(children).some((t) => t.toLowerCase() === typed.toLowerCase())
  return (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}>
      {showSearch && <SelectSearch value={query} onChange={setQuery} />}
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn("p-1", position === "popper" &&
          "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]")}>
        <SearchContext.Provider value={showSearch ? query.trim().toLowerCase() : ""}>
          {children}
        </SearchContext.Provider>
        {canAdd && (
          <SelectPrimitive.Item
            value={ADD_PREFIX + typed}
            className="relative flex w-full cursor-default select-none items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-primary outline-none focus:bg-accent">
            <Plus className="h-4 w-4" />
            <SelectPrimitive.ItemText>{addLabel} “{typed}”</SelectPrimitive.ItemText>
          </SelectPrimitive.Item>
        )}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-3 py-2 text-xs font-medium text-muted-foreground", className)}
    {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef(({ className, children, disabled, ...props }, ref) => {
  const query = React.useContext(SearchContext)
  const hidden = Boolean(query) && !textOf(children).toLowerCase().includes(query)
  return (
  <SelectPrimitive.Item
    ref={ref}
    disabled={disabled || hidden}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-md px-3 py-2 pr-8 text-sm text-foreground outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      hidden && "hidden",
      className
    )}
    {...props}>
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
  )
})
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props} />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  textOf,
  countItems,
  SEARCH_FROM,
  addedValue,
}
